import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { MenuGroup, Section } from '@atlaskit/menu';
import { Inline, Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import Popup from '@databases/atlaskit/Popup';
import { tooltip } from '@databases/keyboard-shortcuts';
import React, {
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import ButtonItem from './ButtonItem';
import { type DrilldownMenu, isDrilldownMenu } from './DrilldownMenu';
import { ElementBeforeIconWrapper, ElementAfterShortCutWrapper } from './components';
import { getMenuItemIcon } from './get-menu-item-icon';
import type { MenuNav } from './hooks/useMenuNav';
import type { SubmenuState } from './hooks/useSubmenuState';
import { useSharedSubmenuState } from './hooks/useSubmenuState';
import {
  HOVER_INTENT_DELAY_MS,
  SUBMENU_OFFSET,
  SUBMENU_PLACEMENT,
} from './menu-constants';
import type { MenuItem } from './types';

const wrapIconBefore = (icon: React.ReactElement | undefined) =>
  icon ? <ElementBeforeIconWrapper>{icon}</ElementBeforeIconWrapper> : undefined;

const ValueDisplayWrapper: FunctionComponent<{ children: React.ReactNode }> = ({
  children,
}) => <Text color="color.text.subtlest">{children}</Text>;

export interface MenuItemRowProps {
  item: MenuItem;
  nav: MenuNav;
  submenuState: SubmenuState;
  onClose?: (event?: Event | React.MouseEvent | React.KeyboardEvent) => void;
  isInsideSubmenu?: boolean;
  hasCheckedSibling?: boolean;
}

export interface SubmenuRowBaseProps {
  /** Unique key for this submenu row, for mutual exclusion tracking. */
  submenuKey: string;
  content: string;
  elemBefore?: React.ReactElement;
  elemAfter?: React.ReactElement;
  valueDisplay?: React.ReactNode;
  /** Passed through to the submenuContent render-prop. */
  nav: MenuNav;
  isDisabled?: boolean;
  /** Optional primary action when the row itself is clicked (rare). */
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

export type SubmenuRowProps = SubmenuRowBaseProps &
  (
    | { submenuContent: (nav: MenuNav) => ReactNode; submenuItems?: never }
    | { submenuItems: MenuItem[]; submenuContent?: never }
  );

const SubmenuItemsList = ({
  items,
  nav,
  onItemClick,
}: {
  items: MenuItem[];
  nav: MenuNav;
  onItemClick?: () => void;
}) => {
  const submenuState = useSharedSubmenuState();
  if (!submenuState) {
    throw new Error(
      'SubmenuItemsList must be rendered inside a SharedSubmenuStateContext provider.'
    );
  }
  const hasCheckedSibling = items.some((item) => item.isChecked === true);

  return (
    <MenuGroup spacing="compact">
      <Section>
        {items.map((item) => (
          <MenuItemRow
            key={item.key}
            item={item}
            nav={nav}
            submenuState={submenuState}
            onClose={onItemClick}
            isInsideSubmenu
            hasCheckedSibling={hasCheckedSibling}
          />
        ))}
      </Section>
    </MenuGroup>
  );
};

/**
 * A hover-to-open submenu item for use inside free-form `content` menu pages.
 *
 * Uses `SharedSubmenuStateContext` for mutual exclusion — hovering one `SubmenuRow`
 * closes any other open submenu at the same level.
 */
export const SubmenuRow = (props: SubmenuRowProps) => {
  const {
    submenuKey,
    content,
    elemBefore,
    elemAfter,
    valueDisplay,
    nav,
    isDisabled = false,
    onClick,
  } = props;
  const state = useSharedSubmenuState();

  if (!state) {
    throw new Error(
      'SubmenuRow must be rendered inside a SharedSubmenuStateContext provider.'
    );
  }

  const isOpen = state.isOpen(submenuKey);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = setTimeout(() => {
      state.open(submenuKey);
    }, HOVER_INTENT_DELAY_MS);
  }, [isDisabled, state, submenuKey]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (onClick) {
        onClick(e);
      } else {
        if (isOpen) {
          state.close(submenuKey);
        } else {
          state.open(submenuKey);
        }
      }
    },
    [onClick, isOpen, state, submenuKey]
  );

  const handleSubmenuClose = useCallback(() => {
    state.close(submenuKey);
  }, [state, submenuKey]);

  const renderSubmenuContent = useCallback(() => {
    if ('submenuItems' in props && props.submenuItems) {
      return (
        <SubmenuItemsList
          items={props.submenuItems}
          nav={nav}
          onItemClick={handleSubmenuClose}
        />
      );
    }
    if ('submenuContent' in props && props.submenuContent) {
      return props.submenuContent(nav);
    }
    return null;
  }, [props, nav, handleSubmenuClose]);

  return (
    <Popup
      isOpen={isOpen}
      placement={SUBMENU_PLACEMENT}
      offset={SUBMENU_OFFSET}
      onClose={handleSubmenuClose}
      contentContainer={null}
      content={renderSubmenuContent}
      trigger={(triggerProps) => (
        <ButtonItem
          {...triggerProps}
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          isSelected={isOpen}
          isDisabled={isDisabled}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          iconBefore={wrapIconBefore(elemBefore)}
          iconAfter={
            elemAfter || (
              <Inline alignBlock="center" space="space.075">
                {valueDisplay && (
                  <ValueDisplayWrapper>{valueDisplay}</ValueDisplayWrapper>
                )}
                <ChevronRightIcon size="small" spacing="none" label="" />
              </Inline>
            )
          }
          testId={`submenu-row-${submenuKey}`}
        >
          {content}
        </ButtonItem>
      )}
    />
  );
};

const MenuItemRow: FunctionComponent<MenuItemRowProps> = ({
  item,
  nav,
  submenuState,
  onClose,
  isInsideSubmenu = false,
  hasCheckedSibling = false,
}) => {
  const { closeAll: closeAllSubmenus } = submenuState;
  const content = item.content;

  const handleClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (isDrilldownMenu(content)) {
        const {
          title,
          width,
          content: drilldownContent,
          items: drilldownItems,
        } = content;
        nav.push(
          drilldownContent != null
            ? { title, width, content: drilldownContent }
            : { title, width, items: drilldownItems }
        );
        item.onClick?.(e);
        return;
      }

      item.onClick?.(e);
      if (isInsideSubmenu ? item.closeOnClick ?? true : item.closeOnClick) {
        onClose?.(e);
      }
    },
    [content, nav, item, onClose, isInsideSubmenu]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!isInsideSubmenu) {
        closeAllSubmenus();
      }
      item.onMouseEnter?.(e);
    },
    [item, closeAllSubmenus, isInsideSubmenu]
  );

  const hasDrilldownContent = isDrilldownMenu(content);

  const renderItem = () => {
    // Hover-to-open submenu
    if (item.submenuContent != null || item.submenuItems != null) {
      const baseProps = {
        submenuKey: item.key,
        content: typeof content === 'string' ? content : item.name ?? item.key,
        elemBefore: getMenuItemIcon(item, hasCheckedSibling),
        elemAfter: item.elemAfter,
        valueDisplay: item.valueDisplay,
        isDisabled: item.isDisabled,
        onClick: item.onClick,
        nav,
      };

      if (item.submenuItems != null) {
        return <SubmenuRow {...baseProps} submenuItems={item.submenuItems} />;
      }
      return <SubmenuRow {...baseProps} submenuContent={item.submenuContent} />;
    }

    const shouldRenderAsButton =
      hasDrilldownContent || typeof content === 'string' || item.onClick;
    if (shouldRenderAsButton) {
      const ariaProps =
        item.isChecked !== undefined
          ? { role: 'menuitemradio' as const, 'aria-checked': item.isChecked }
          : { role: 'menuitem' as const };

      const displayContent = hasDrilldownContent
        ? item.name || (content as DrilldownMenu).title
        : content;

      const shortcutDisplay = item.shortcut ? tooltip(item.shortcut.keyMap) : null;
      const shortcutElement = shortcutDisplay ? (
        <ElementAfterShortCutWrapper tooltip={shortcutDisplay} />
      ) : null;
      const valueDisplayElement = item.valueDisplay ? (
        <ValueDisplayWrapper>{item.valueDisplay}</ValueDisplayWrapper>
      ) : null;

      return (
        <ButtonItem
          {...ariaProps}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={item.onMouseLeave}
          iconBefore={wrapIconBefore(getMenuItemIcon(item, hasCheckedSibling))}
          iconAfter={item.elemAfter || shortcutElement || valueDisplayElement}
          isDisabled={item.isDisabled}
          isSelected={item.isActive}
          testId={item.key}
        >
          {displayContent}
        </ButtonItem>
      );
    }

    // Raw React content
    return <>{content}</>;
  };

  return (
    <Tooltip content={item.tooltipDescription} position={item.tooltipPosition}>
      {renderItem()}
    </Tooltip>
  );
};

export default MenuItemRow;
